/**
 * Identity = anon uuid + a chosen handle. No login/auth (hard rule).
 *
 * Stored in a cookie on the PARENT domain (.karrtopelka.com) so every game subdomain
 * shares ONE identity — play reflex, then aim, same anonId, handle typed once.
 * localStorage is kept as a migration source + fallback (cookies blocked, local dev
 * on a bare host). First subdomain visited after this ships wins the migration; old
 * identities on other subdomains orphan their board rows — accepted, toys.
 *
 * Clearing cookies resets identity/streak — accepted (leaderboards are toys).
 */

const ANON_KEY = "kp:anonId";
const HANDLE_KEY = "kp:handle";
const ANON_COOKIE = "kp_anon";
const HANDLE_COOKIE = "kp_handle";
/** 2 years; refreshed on every visit, so it only lapses after 2y of absence. */
const COOKIE_MAX_AGE_S = 60 * 60 * 24 * 730;

// Module-level cache: identity is stable for the lifetime of the page even when both
// cookie and localStorage are blocked (previously each call minted a fresh uuid).
let cachedAnonId: string | null = null;
let cachedHandle: string | null = null;

function safeLocalStorage(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null; // SSR / blocked storage
  }
}

/** ".karrtopelka.com" on any prod subdomain; null (host-only cookie) elsewhere. */
function cookieDomain(): string | null {
  if (typeof location === "undefined") return null;
  const host = location.hostname;
  if (host === "karrtopelka.com" || host.endsWith(".karrtopelka.com")) {
    return ".karrtopelka.com";
  }
  return null;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split("; ")) {
    const eq = part.indexOf("=");
    if (eq > 0 && part.slice(0, eq) === name) {
      try {
        const v = decodeURIComponent(part.slice(eq + 1));
        return v.length > 0 ? v : null;
      } catch {
        return null;
      }
    }
  }
  return null;
}

function writeCookie(name: string, value: string): void {
  if (typeof document === "undefined") return;
  const domain = cookieDomain();
  let cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE_S}; samesite=lax`;
  if (domain) cookie += `; domain=${domain}`;
  if (typeof location !== "undefined" && location.protocol === "https:") {
    cookie += "; secure";
  }
  document.cookie = cookie;
}

/** Stable cross-subdomain anon id; created on first call, refreshed on every visit. */
export function getAnonId(): string {
  if (cachedAnonId) return cachedAnonId;
  const ls = safeLocalStorage();
  // Cookie first (shared across subdomains), then localStorage (pre-cookie identities
  // migrate forward), then mint.
  const id = readCookie(ANON_COOKIE) ?? ls?.getItem(ANON_KEY) ?? crypto.randomUUID();
  cachedAnonId = id;
  writeCookie(ANON_COOKIE, id);
  ls?.setItem(ANON_KEY, id); // keep the fallback in sync
  return id;
}

export function getHandle(): string | null {
  if (cachedHandle) return cachedHandle;
  const fromCookie = readCookie(HANDLE_COOKIE);
  const ls = safeLocalStorage();
  const handle = fromCookie ?? ls?.getItem(HANDLE_KEY) ?? null;
  if (handle) {
    cachedHandle = handle;
    if (!fromCookie) writeCookie(HANDLE_COOKIE, handle); // migrate forward
    ls?.setItem(HANDLE_KEY, handle);
  }
  return handle;
}

export function setHandle(handle: string): void {
  cachedHandle = handle;
  writeCookie(HANDLE_COOKIE, handle);
  safeLocalStorage()?.setItem(HANDLE_KEY, handle);
}

export function hasHandle(): boolean {
  return getHandle() !== null;
}
